import React, { useState } from "react";
import {
  Row, Col, Alert, Card, CardBody, Container,
  Input, Label, Form, FormFeedback,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";

import logo from "assets/images/logo.svg";
import {
  sendOtpRequest,
  verifyOtpRequest,
  resetPasswordRequest,
} from "helpers/auth_client";

// OTP ve şifre sıfırlama artık sunucuda doğrulanıyor.
const OTP_PURPOSE = "password-reset";

const StepIndicator = ({ step }) => (
  <div className="d-flex align-items-center justify-content-center mb-4">
    {[1, 2, 3].map((s, i) => (
      <React.Fragment key={s}>
        <div
          style={{
            width: 32, height: 32, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700,
            background: step >= s ? "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)" : "#e9ecef",
            color: step >= s ? "#fff" : "#adb5bd",
            transition: "all 0.3s",
            boxShadow: step >= s ? "0 4px 12px rgba(85,110,230,0.4)" : "none",
          }}
        >
          {step > s ? <i className="bx bx-check" style={{ fontSize: 16 }} /> : s}
        </div>
        {i < 2 && (
          <div
            style={{
              flex: 1, height: 2, margin: "0 6px",
              background: step > s ? "linear-gradient(90deg, #556ee6, #6f42c1)" : "#e9ecef",
              transition: "all 0.3s",
            }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

const STEP_LABELS = ["Telefon Numarası", "Kod Doğrulama", "Yeni Şifre"];

const ForgetPasswordPage = () => {
  document.title = "Şifremi Unuttum | LojGo";
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [formattedPhone, setFormattedPhone] = useState("");
  const [smsLoading, setSmsLoading] = useState(false);

  // Sunucunun "bu numara az önce doğrulandı" bileti. Şifre sıfırlama yalnızca bununla yapılır.
  const [verificationTicket, setVerificationTicket] = useState("");

  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSendSms = async (e) => {
    e.preventDefault();
    const cleaned = phone.replace(/\D/g, "");
    if (!cleaned || cleaned.length < 10) {
      setPhoneError("Geçerli bir telefon numarası girin (örn: 5321234567)");
      return;
    }
    setSmsLoading(true);
    setPhoneError("");
    try {
      // Numaranın kayıtlı olup olmadığı bilgisi bilerek dışarı verilmiyor:
      // sunucu her iki durumda da aynı yanıtı döner, SMS yalnızca kayıtlıysa gider.
      // Böylece bu ekran bir "hangi numaralar sistemde var" tarayıcısına dönüşmez.
      await sendOtpRequest(phone, OTP_PURPOSE);
      setFormattedPhone(phone);
      setStep(2);
    } catch (err) {
      setPhoneError(err.message || "SMS gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setSmsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setOtpError("Lütfen doğrulama kodunu girin.");
      return;
    }
    setOtpLoading(true);
    setOtpError("");
    try {
      const result = await verifyOtpRequest(phone, otp, OTP_PURPOSE);
      if (result?.verificationTicket) {
        setVerificationTicket(result.verificationTicket);
        setStep(3);
      } else {
        setOtpError("Kod doğrulanamadı. Tekrar deneyin.");
      }
    } catch (err) {
      setOtpError(err.message || "Kod hatalı. Tekrar deneyin.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendSms = async () => {
    setSmsLoading(true);
    setOtpError("");
    try {
      await sendOtpRequest(phone, OTP_PURPOSE);
    } catch (err) {
      setOtpError(err.message || "SMS tekrar gönderilemedi.");
    } finally {
      setSmsLoading(false);
    }
  };

  const passwordValidation = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: Yup.object({
      password: Yup.string().min(6, "Şifre en az 6 karakter olmalıdır").required("Yeni şifre zorunludur"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Şifreler eşleşmiyor")
        .required("Şifre tekrarı zorunludur"),
    }),
    onSubmit: async (values) => {
      setSubmitLoading(true);
      try {
        // Eskiden burada doğrudan PATCH /Users(id) atılıyordu. API'nin OTP'den haberi
        // olmadığı için, bu ekranı hiç açmadan aynı isteği gönderen biri herhangi bir
        // kullanıcının (admin dahil) şifresini değiştirebiliyordu. Artık sunucu, kendi
        // ürettiği tek kullanımlık bilet olmadan şifre değiştirmiyor.
        await resetPasswordRequest(phone, values.password, verificationTicket);

        setSuccessMsg("Şifreniz başarıyla güncellendi! Giriş sayfasına yönlendiriliyorsunuz...");
        setTimeout(() => navigate("/login"), 2000);
      } catch (err) {
        passwordValidation.setFieldError(
          "password",
          err.message || "Şifre güncellenirken hata oluştu."
        );
      } finally {
        setSubmitLoading(false);
      }
    },
  });

  return (
    <React.Fragment>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7ff 0%, #e8eeff 50%, #f0f4ff 100%)",
          display: "flex", alignItems: "center",
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>

              {/* Logo */}
              <div className="text-center mb-4">
                <Link to="/">
                  <div
                    style={{
                      width: 64, height: 64, borderRadius: 18,
                      background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 8px 24px rgba(85,110,230,0.4)",
                      marginBottom: 12,
                    }}
                  >
                    <img src={logo} alt="LojGo" height="36" style={{ filter: "brightness(0) invert(1)" }} />
                  </div>
                </Link>
                <h3 style={{ fontWeight: 700, color: "#1a1a2e", marginBottom: 4 }}>Şifremi Unuttum</h3>
                <p style={{ color: "#6c757d", fontSize: 15 }}>{STEP_LABELS[step - 1]}</p>
              </div>

              <Card
                style={{
                  border: "none", borderRadius: 20,
                  boxShadow: "0 20px 60px rgba(85,110,230,0.12)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: 4,
                    background: "linear-gradient(90deg, #556ee6 0%, #6f42c1 50%, #e83e8c 100%)",
                  }}
                />

                <CardBody style={{ padding: "2rem 2.5rem" }}>
                  <StepIndicator step={step} />

                  {successMsg && (
                    <Alert color="success" className="d-flex align-items-center gap-2">
                      <i className="bx bx-check-circle fs-5" />
                      {successMsg}
                    </Alert>
                  )}

                  {/* ─── Adım 1: Telefon ─── */}
                  {step === 1 && (
                    <Form onSubmit={handleSendSms}>
                      <div
                        style={{
                          background: "linear-gradient(135deg, #eef1ff 0%, #f0ebff 100%)",
                          borderRadius: 12, padding: "14px 16px", marginBottom: 24,
                          border: "1px solid #d6dbff",
                        }}
                      >
                        <div className="d-flex align-items-start gap-2">
                          <i className="bx bx-info-circle text-primary mt-1" style={{ fontSize: 18 }} />
                          <p className="mb-0" style={{ fontSize: 13, color: "#555", lineHeight: 1.5 }}>
                            Kayıtlı telefon numaranızı girin. SMS ile doğrulama kodu göndereceğiz.
                          </p>
                        </div>
                      </div>

                      {phoneError && (
                        <Alert color="danger" className="d-flex align-items-center gap-2">
                          <i className="bx bx-x-circle fs-5" />
                          {phoneError}
                        </Alert>
                      )}

                      <div className="mb-4">
                        <Label className="form-label fw-semibold" style={{ color: "#344050", fontSize: 14 }}>
                          Telefon Numarası
                        </Label>
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                              border: "none", color: "#fff", borderRadius: "10px 0 0 10px",
                              padding: "0 14px",
                            }}
                          >
                            <i className="bx bx-phone" style={{ fontSize: 18 }} />
                          </span>
                          <Input
                            type="tel"
                            placeholder="5321234567"
                            value={phone}
                            onChange={(e) => { setPhone(e.target.value); setPhoneError(""); }}
                            style={{
                              borderLeft: "none", borderRadius: "0 10px 10px 0",
                              padding: "12px 14px", fontSize: 15,
                            }}
                            invalid={!!phoneError}
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={smsLoading}
                        style={{
                          width: "100%", padding: "12px",
                          background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                          border: "none", borderRadius: 12, color: "#fff",
                          fontWeight: 700, fontSize: 15,
                          boxShadow: "0 8px 20px rgba(85,110,230,0.4)",
                        }}
                      >
                        {smsLoading ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Kontrol ediliyor...</>
                        ) : (
                          <><i className="bx bx-send me-2" />Doğrulama Kodu Gönder</>
                        )}
                      </button>
                    </Form>
                  )}

                  {/* ─── Adım 2: OTP ─── */}
                  {step === 2 && (
                    <div>
                      <div className="text-center mb-4">
                        <div
                          style={{
                            width: 80, height: 80, borderRadius: 20,
                            background: "linear-gradient(135deg, #eef1ff 0%, #f0ebff 100%)",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            marginBottom: 16, border: "2px solid #d6dbff",
                          }}
                        >
                          <i className="bx bx-message-detail" style={{ fontSize: 36, color: "#556ee6" }} />
                        </div>
                        <h5 style={{ fontWeight: 700, color: "#1a1a2e" }}>SMS Gönderildi</h5>
                        <p className="text-muted" style={{ fontSize: 14 }}>
                          <strong style={{ color: "#556ee6" }}>{formattedPhone}</strong> numarasına
                          6 haneli doğrulama kodu gönderdik.
                        </p>
                      </div>

                      {otpError && (
                        <Alert color="danger" className="d-flex align-items-center gap-2">
                          <i className="bx bx-x-circle fs-5" />
                          {otpError}
                        </Alert>
                      )}

                      <div className="mb-4">
                        <Label className="form-label fw-semibold text-dark text-center d-block">
                          Doğrulama Kodu
                        </Label>
                        <Input
                          type="text"
                          placeholder="• • • • • •"
                          maxLength={6}
                          value={otp}
                          onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "")); setOtpError(""); }}
                          style={{
                            textAlign: "center", fontSize: 28, fontWeight: 700,
                            letterSpacing: 12, borderRadius: 12, padding: "14px",
                            border: "2px solid #dee2e6", color: "#556ee6",
                          }}
                        />
                      </div>

                      <button
                        onClick={handleVerifyOtp}
                        disabled={otpLoading || otp.length < 6}
                        style={{
                          width: "100%", padding: "12px",
                          background: otp.length === 6
                            ? "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)"
                            : "#e9ecef",
                          border: "none", borderRadius: 12,
                          color: otp.length === 6 ? "#fff" : "#adb5bd",
                          fontWeight: 700, fontSize: 15,
                          boxShadow: otp.length === 6 ? "0 8px 20px rgba(85,110,230,0.4)" : "none",
                          transition: "all 0.2s",
                        }}
                      >
                        {otpLoading ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Doğrulanıyor...</>
                        ) : (
                          <><i className="bx bx-check-shield me-2" />Kodu Doğrula</>
                        )}
                      </button>

                      <div className="text-center mt-3 d-flex justify-content-center gap-3">
                        <button
                          className="btn btn-link p-0"
                          style={{ fontSize: 13, color: "#6c757d", textDecoration: "none" }}
                          onClick={handleResendSms}
                          disabled={smsLoading}
                        >
                          <i className="bx bx-refresh me-1" />
                          {smsLoading ? "Gönderiliyor..." : "Kodu tekrar gönder"}
                        </button>
                        <span style={{ color: "#dee2e6" }}>|</span>
                        <button
                          className="btn btn-link p-0"
                          style={{ fontSize: 13, color: "#6c757d", textDecoration: "none" }}
                          onClick={() => { setStep(1); setOtp(""); setOtpError(""); }}
                        >
                          <i className="bx bx-arrow-back me-1" />
                          Geri dön
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ─── Adım 3: Yeni Şifre ─── */}
                  {step === 3 && (
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        passwordValidation.handleSubmit();
                      }}
                    >
                      <div
                        style={{
                          background: "linear-gradient(135deg, #eef1ff 0%, #f0ebff 100%)",
                          borderRadius: 12, padding: "10px 14px", marginBottom: 20,
                          display: "flex", alignItems: "center", gap: 10,
                          border: "1px solid #d6dbff",
                        }}
                      >
                        <i className="bx bx-check-circle" style={{ fontSize: 20, color: "#556ee6" }} />
                        <div>
                          <div style={{ fontSize: 12, color: "#6c757d" }}>Doğrulanan numara</div>
                          <div style={{ fontWeight: 700, color: "#556ee6", fontSize: 14 }}>{formattedPhone}</div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <Label className="form-label fw-semibold" style={{ color: "#344050", fontSize: 14 }}>
                          Yeni Şifre
                        </Label>
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                              border: "none", color: "#fff", borderRadius: "10px 0 0 10px",
                              padding: "0 14px",
                            }}
                          >
                            <i className="bx bx-lock-alt" style={{ fontSize: 18 }} />
                          </span>
                          <Input
                            name="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="En az 6 karakter"
                            onChange={passwordValidation.handleChange}
                            onBlur={passwordValidation.handleBlur}
                            value={passwordValidation.values.password}
                            invalid={!!(passwordValidation.touched.password && passwordValidation.errors.password)}
                            style={{ borderLeft: "none", borderRight: "none", borderRadius: 0, padding: "12px 14px" }}
                          />
                          <span
                            className="input-group-text"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              background: "#f8f9ff", border: "1.5px solid #dee2e6", borderLeft: "none",
                              borderRadius: "0 10px 10px 0", cursor: "pointer", padding: "0 14px",
                            }}
                          >
                            <i
                              className={showPassword ? "bx bx-hide" : "bx bx-show"}
                              style={{ fontSize: 18, color: "#8592c0" }}
                            />
                          </span>
                          {passwordValidation.touched.password && passwordValidation.errors.password && (
                            <FormFeedback>{passwordValidation.errors.password}</FormFeedback>
                          )}
                        </div>
                      </div>

                      <div className="mb-4">
                        <Label className="form-label fw-semibold" style={{ color: "#344050", fontSize: 14 }}>
                          Şifre Tekrar
                        </Label>
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                              border: "none", color: "#fff", borderRadius: "10px 0 0 10px",
                              padding: "0 14px",
                            }}
                          >
                            <i className="bx bx-lock" style={{ fontSize: 18 }} />
                          </span>
                          <Input
                            name="confirmPassword"
                            type={showConfirm ? "text" : "password"}
                            placeholder="Yeni şifrenizi tekrar girin"
                            onChange={passwordValidation.handleChange}
                            onBlur={passwordValidation.handleBlur}
                            value={passwordValidation.values.confirmPassword}
                            invalid={!!(passwordValidation.touched.confirmPassword && passwordValidation.errors.confirmPassword)}
                            style={{ borderLeft: "none", borderRight: "none", borderRadius: 0, padding: "12px 14px" }}
                          />
                          <span
                            className="input-group-text"
                            onClick={() => setShowConfirm(!showConfirm)}
                            style={{
                              background: "#f8f9ff", border: "1.5px solid #dee2e6", borderLeft: "none",
                              borderRadius: "0 10px 10px 0", cursor: "pointer", padding: "0 14px",
                            }}
                          >
                            <i
                              className={showConfirm ? "bx bx-hide" : "bx bx-show"}
                              style={{ fontSize: 18, color: "#8592c0" }}
                            />
                          </span>
                          {passwordValidation.touched.confirmPassword && passwordValidation.errors.confirmPassword && (
                            <FormFeedback>{passwordValidation.errors.confirmPassword}</FormFeedback>
                          )}
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={submitLoading}
                        style={{
                          width: "100%", padding: "12px",
                          background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                          border: "none", borderRadius: 12, color: "#fff",
                          fontWeight: 700, fontSize: 15,
                          boxShadow: "0 8px 20px rgba(85,110,230,0.4)",
                        }}
                      >
                        {submitLoading ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Güncelleniyor...</>
                        ) : (
                          <><i className="bx bx-check me-2" />Şifremi Güncelle</>
                        )}
                      </button>
                    </Form>
                  )}
                </CardBody>
              </Card>

              <div className="text-center mt-4">
                <p style={{ color: "#6c757d", fontSize: 14 }}>
                  <Link
                    to="/login"
                    style={{ color: "#556ee6", fontWeight: 600, textDecoration: "none" }}
                  >
                    <i className="bx bx-arrow-back me-1" />
                    Giriş sayfasına dön
                  </Link>
                </p>
                <p style={{ color: "#adb5bd", fontSize: 12 }}>
                  &copy; {new Date().getFullYear()} LojGo. Tüm hakları saklıdır.
                </p>
              </div>

            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ForgetPasswordPage;
